/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { INodeProps } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IAdditionalContentData, ILinksData } from '../mod-video-player.props.autogenerated';

/**
 * IVideoPlayerAdditionalContentProps: Interface for
 * Video Player Additional Content Component props.
 */
export interface IVideoPlayerAdditionalContentProps {
    requestContext: Msdyn365.IRequestContext;
    additionalContent: IAdditionalContentData | undefined;
    handleAdditionalTextChange(event: Msdyn365.ContentEditableEvent): void;
    handleAdditionalParagraphChange(event: Msdyn365.ContentEditableEvent): void;
    handleAdditionalLinkTextChange(linkIndex: number, event: Msdyn365.ContentEditableEvent): void;
}

/**
 * IVideoPlayerAdditionalContentProps: Interface for
 * Video Player Additional Content view props.
 */
export interface IVideoPlayerAdditionalContentViewProps {
    additionalContentNode: INodeProps;
    additionalContentItems?: IVideoPlayerAdditionalContentItemViewProps;
}

/**
 * IVideoPlayerAdditionalContentProps: Interface for
 * Video Player Additional Content Item view props.
 */
export interface IVideoPlayerAdditionalContentItemViewProps {
    heading: React.ReactNode;
    text: React.ReactNode;
    links: React.ReactNode;
    additionalContentItemContainer: INodeProps;
    additionalContentItemLinks: INodeProps;
}

/**
 * Renders additional content heading.
 * @param additionalContentHeading - Additional content heading.
 * @param props - Video Player Additional content component props.
 * @returns Heading Node.
 */
const renderHeading = (additionalContentHeading: string, props: IVideoPlayerAdditionalContentProps) => {
    const requestContext = props.requestContext;
    return (
        <Msdyn365.Text
            text={additionalContentHeading}
            tag='p'
            className='ms-video-player__details__additional-content__text'
            editProps={{
                /**
                 * On edit event for heading text change.
                 * @param event - Change event.
                 */
                onEdit: event => {
                    props.handleAdditionalTextChange(event);
                },
                requestContext
            }}
        />
    );
};

/**
 * Renders additional content heading.
 * @param additionalContentParagraphText - Additional content paragraph text.
 * @param props - Video Player Additional content component props.
 * @returns Paragraph Node.
 */
const renderParagraph = (additionalContentParagraphText: string, props: IVideoPlayerAdditionalContentProps) => {
    const requestContext = props.requestContext;
    return (
        <Msdyn365.Text
            text={additionalContentParagraphText}
            tag='p'
            className='ms-video-player__details__additional-content__paragraph'
            editProps={{
                /**
                 * On edit event for heading text change.
                 * @param event - Change event.
                 */
                onEdit: event => {
                    props.handleAdditionalParagraphChange(event);
                },
                requestContext
            }}
        />
    );
};

/**
 * Renders additional content heading.
 * @param ctaLink - Additional content link.
 * @param props - Video Player Additional content component props.
 * @param linkIndex - Additional content link index.
 * @returns Link Node.
 */
const renderLinks = (ctaLink: ILinksData, props: IVideoPlayerAdditionalContentProps, linkIndex: number) => {
    const editableLink: Msdyn365.ILinksData = {
        ariaLabel: ctaLink.ariaLabel,
        className: 'ms-video-player__details__additional-content__cta__links',
        linkText: ctaLink.linkText,
        linkUrl: ctaLink.linkUrl.destinationUrl,
        openInNewTab: ctaLink.openInNewTab,
        role: 'link'
    };
    const requestContext = props.requestContext;
    return (
        <Msdyn365.Link
            key={linkIndex}
            link={editableLink}
            editProps={{
                /**
                 * Handle additional Link Text change.
                 * @param event - The keyboard event.
                 */
                onTextChange: event => {
                    props.handleAdditionalLinkTextChange(linkIndex, event);
                },
                requestContext
            }}
        />
    );
};

/**
 * Create node for each additonal content item.
 * @param item - Video Player additonal content item props.
 * @param props - Video Player Additional content component props.
 * @returns IVideoPlayerAdditionalContentItemViewProps.
 */
const getAdditionalContentItems = (
    item: IAdditionalContentData,
    props: IVideoPlayerAdditionalContentProps
): IVideoPlayerAdditionalContentItemViewProps => {
    const headingNode = item.heading && renderHeading(item.heading, props);
    const paragraphNode = item.subtext && renderParagraph(item.subtext, props);
    const linksNode = item.links?.map((ctaLink: ILinksData, index: number) => {
        return renderLinks(ctaLink, props, index);
    });

    return {
        heading: headingNode,
        text: paragraphNode,
        links: linksNode,
        additionalContentItemContainer: { className: 'ms-video-player__details__additional-content__container' },
        additionalContentItemLinks: { className: 'ms-video-player__details__additional-content__cta' }
    };
};

/**
 * VideoPlayerAdditionalContent component.
 * @param props - Video Player Additional content component props.
 * @returns Video Player Additional content view props.
 */
export const videoPlayerAdditionalContent = (
    props: IVideoPlayerAdditionalContentProps
): IVideoPlayerAdditionalContentViewProps | undefined => {
    const { additionalContent } = props;
    if (!additionalContent) {
        return undefined;
    }

    const reactNodes: IVideoPlayerAdditionalContentItemViewProps = getAdditionalContentItems(additionalContent, props);
    return {
        additionalContentNode: { className: 'ms-video-player__details__additional-content' },
        additionalContentItems: reactNodes
    };
};
